import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Zap, Calendar, Clock, Crown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * QuotaCard — Displays monthly + daily AI chat quota with progress bars.
 * Auto-fetches /api/plan/limits. Can be passed `data` prop to skip the fetch.
 */
export const QuotaCard = ({ data: propData, compact = false }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(propData || null);
  const [loading, setLoading] = useState(!propData);

  useEffect(() => {
    if (propData) {
      setData(propData);
      return;
    }
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/plan/limits`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        setData(res.data);
      } catch (e) {
        console.error('QuotaCard: fetch failed', e);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchData();
  }, [token, propData]);

  if (loading || !data) {
    return (
      <div className="animate-pulse bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-5 h-32" data-testid="quota-card-loading" />
    );
  }

  const { plan, usage } = data;
  const monthlyPct = usage.monthly_limit > 0 ? Math.min(100, (usage.monthly_used / usage.monthly_limit) * 100) : 0;
  const dailyPct = usage.daily_limit > 0 ? Math.min(100, (usage.daily_used / usage.daily_limit) * 100) : 0;

  const barColor = (pct) => {
    if (pct >= 95) return 'bg-red-500';
    if (pct >= 80) return 'bg-amber-500';
    if (pct >= 60) return 'bg-yellow-400';
    return 'bg-emerald-500';
  };

  const planLabel = {
    trial: 'Trial',
    starter: 'Starter',
    growth: 'Growth',
    business: 'Business',
    enterprise: 'Enterprise'
  }[plan] || plan;

  const formatTime = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('id-ID', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    } catch { return '—'; }
  };

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-5" data-testid="quota-card">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-indigo-50 dark:bg-indigo-950 rounded-lg">
            <Zap className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-zinc-50 text-sm">Kuota AI</h3>
            <p className="text-xs text-slate-500 dark:text-zinc-400">Pesan AI per hari & bulan</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gradient-to-r from-indigo-50 to-violet-50 dark:from-indigo-950 dark:to-violet-950 rounded-full border border-indigo-200 dark:border-indigo-800">
          <Crown className="w-3 h-3 text-indigo-600 dark:text-indigo-400" />
          <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300" data-testid="quota-plan-label">{planLabel}</span>
        </div>
      </div>

      <div className="space-y-4">
        {/* Monthly */}
        <div data-testid="quota-monthly">
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-slate-500 dark:text-zinc-400" />
              <span className="text-xs text-slate-600 dark:text-zinc-400">Bulan ini</span>
            </div>
            <span className="text-xs font-medium text-slate-700 dark:text-zinc-300 tabular-nums">
              <span data-testid="quota-monthly-used">{usage.monthly_used.toLocaleString('id-ID')}</span>
              <span className="text-slate-500 dark:text-zinc-400"> / {usage.monthly_limit.toLocaleString('id-ID')}</span>
              <span className="ml-1.5 text-slate-500 dark:text-zinc-400">({monthlyPct.toFixed(0)}%)</span>
            </span>
          </div>
          <div className="h-1.5 bg-slate-100 dark:bg-zinc-800 rounded-full overflow-hidden">
            <div
              className={`h-full ${barColor(monthlyPct)} transition-all duration-500`}
              style={{ width: `${monthlyPct}%` }}
              data-testid="quota-monthly-bar"
            />
          </div>
          {!compact && usage.next_monthly_reset && (
            <p className="text-[11px] text-slate-500 mt-1 dark:text-zinc-400">Reset: {formatTime(usage.next_monthly_reset)}</p>
          )}
        </div>

        {/* Daily */}
        <div data-testid="quota-daily">
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-slate-500 dark:text-zinc-400" />
              <span className="text-xs text-slate-600 dark:text-zinc-400">Hari ini</span>
            </div>
            <span className="text-xs font-medium text-slate-700 dark:text-zinc-300 tabular-nums">
              <span data-testid="quota-daily-used">{usage.daily_used.toLocaleString('id-ID')}</span>
              <span className="text-slate-500 dark:text-zinc-400"> / {usage.daily_limit.toLocaleString('id-ID')}</span>
              <span className="ml-1.5 text-slate-500 dark:text-zinc-400">({dailyPct.toFixed(0)}%)</span>
            </span>
          </div>
          <div className="h-1.5 bg-slate-100 dark:bg-zinc-800 rounded-full overflow-hidden">
            <div
              className={`h-full ${barColor(dailyPct)} transition-all duration-500`}
              style={{ width: `${dailyPct}%` }}
              data-testid="quota-daily-bar"
            />
          </div>
          {!compact && (
            <p className="text-[11px] text-slate-500 mt-1 dark:text-zinc-400">Reset tiap 00:00 UTC</p>
          )}
        </div>
      </div>

      {!compact && usage.topup_credits > 0 && (
        <div className="mt-4 flex items-center justify-between px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800" data-testid="quota-topup-credits">
          <span className="text-xs text-emerald-700 dark:text-emerald-300">Kredit tambahan (Pay As You Go)</span>
          <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 tabular-nums">
            +{usage.topup_credits.toLocaleString('id-ID')} pesan
          </span>
        </div>
      )}

      {!compact && (monthlyPct >= 60 || dailyPct >= 60) && plan !== 'business' && plan !== 'enterprise' && (
        <Button
          size="sm"
          variant="outline"
          className="mt-4 w-full text-xs h-8 border-indigo-200 hover:bg-indigo-50 hover:border-indigo-300 text-indigo-600 dark:text-indigo-400 dark:bg-indigo-950/40"
          onClick={() => navigate('/pricing')}
          data-testid="quota-upgrade-btn"
        >
          Upgrade Plan untuk Kuota Lebih Banyak
          <ChevronRight className="w-3 h-3 ml-1" />
        </Button>
      )}

      {!compact && (monthlyPct >= 80 || dailyPct >= 80) && (
        <Button
          size="sm"
          variant="ghost"
          className="mt-2 w-full text-xs h-8 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 dark:text-emerald-400 dark:bg-emerald-950/40"
          onClick={() => navigate('/settings/subscription')}
          data-testid="quota-topup-btn"
        >
          Beli kredit pesan tambahan (Pay As You Go)
          <ChevronRight className="w-3 h-3 ml-1" />
        </Button>
      )}
    </div>
  );
};

export default QuotaCard;
